import { Link } from "react-router-dom";
import { ExtractedField } from "@/components/ExtractedReview";
import { TextField } from "@/components/TextField";
import { SearchSelect } from "@/components/SearchSelect";
import { SelectField } from "@/components/SelectField";
import { PhoneField } from "@/components/PhoneField";
import {
  SectionCard,
  type WizardFieldErrors,
  type WizardStepContext,
} from "@/base";
import { StepLoading } from "@/base/wizard/StepLoading";
import { US_STATE_OPTIONS } from "@/lib/usStates";
import { localTodayIso } from "@/lib/dates";
import type { ManifestField } from "@/lib/brokerProducts";
import { usePriorCarriers } from "@/lib/usePriorCarriers";
import {
  insuredEditable,
  formatEin,
  startDateField,
  WC_ENTITY_TYPE_OPTIONS,
  type WcBusiness,
} from "@/lib/submissions/wc/business";
import type { WcDraft } from "@/lib/submissions/wc/draft";
import { basicFields, type WcAnswer } from "@/lib/submissions/wc/questions";
import {
  useWcManifest,
  WC_MANIFEST_ABSENT,
} from "@/lib/submissions/wc/useWcManifest";
import { QuestionField } from "@/components/submissions/wc/QuestionField";
import { StepLoadProblem } from "@/components/submissions/wc/StepLoadProblem";
import { TWO_COLUMNS } from "@/components/submissions/wc/grids";
import { BusinessReadOnly } from "@/components/submissions/wc/ReadOnlyParts";

// The Workers' Comp wizard's Basic information (BRK-S301, BRK-S388). While
// the insured is being created, the broker fills it in. An existing insured is
// shown as it is on record and never edited (Nav, 2026-10-08): its details
// change only by endorsement, so the page offers the submission's own fields
// and a start date the insured lacks, filled once. Its contacts are on the
// People step since BRK-S391. An uploaded ACORD packet fills the
// insured's fields, each marked until edited (BRK-S306). Each group sits in a
// white section card, as the account form's do, and an insured on record
// shows each value in its field's box (BRK-S349).
//
// What PMS-S808's manifest declares is asked as it declares it (D-10 rule 1):
// the date the business started (the insured's) and the Basic information
// section's fields, such as the years of experience (the submission's). The
// industry isn't asked (Tyler, 2026-10-21: it has no use yet), and a joint
// venture isn't offered for Workers' Comp (the 10/20 call).

const BUSINESS_KEYS: ReadonlySet<string> = new Set<keyof WcBusiness>([
  "legalName",
  "dbaName",
  "state",
  "ein",
  "entityType",
  "emailAddress",
  "phone",
  "businessStartDate",
  "effectiveDate",
]);

/** PMS names the account's phone by its list path (`phones.0.number`). */
const PHONE_PATH = /^phones(\.|$)/;

function errorFor(
  errors: WizardFieldErrors,
  key: keyof WcBusiness,
): string | undefined {
  if (key === "phone" && !errors.phone) {
    return Object.entries(errors).find(([path]) => PHONE_PATH.test(path))?.[1];
  }
  return errors[key];
}

export function BusinessStep({
  draft,
  setDraft,
  setLoaded,
  errors,
  busy,
  extracted,
}: WizardStepContext<WcDraft>) {
  // Read for a new application too, so its page asks what the manifest
  // declares before the application exists.
  const catalog = useWcManifest(draft, setLoaded);
  // Basic information declares no prior carrier: nothing to read.
  const carriers = usePriorCarriers(false);
  // The step draws whole, once the manifest's questions are in, under its
  // placeholder card (BRK-S488). A catalog that can't be read, or has no
  // Workers' Comp manifest, still opens it, and says so below.
  if (draft.manifest === null && catalog.problem === null) {
    return <StepLoading label="Loading the questions…" />;
  }

  const b = draft.business;
  const set = (patch: Partial<WcBusiness>) =>
    setDraft((d) => ({ ...d, business: { ...d.business, ...patch } }));
  /** The broker's edit of one field: its document mark goes. */
  const edit = (key: keyof WcBusiness, patch: Partial<WcBusiness>) => {
    extracted.touch(key);
    set(patch);
  };
  const marked = (key: keyof WcBusiness) => extracted.isPending(key);
  const answer = (key: string, value: WcAnswer) => {
    extracted.touch(key);
    setDraft((d) => ({ ...d, answers: { ...d.answers, [key]: value } }));
  };
  const started = startDateField(draft.manifest);
  const basic = basicFields(draft.manifest);
  const asked = new Set(basic.map((f) => f.key));
  // A refusal on something the page doesn't show still says what it was.
  const elsewhere = Object.entries(errors).filter(
    ([path]) =>
      !BUSINESS_KEYS.has(path) &&
      !asked.has(path) &&
      !PHONE_PATH.test(path) &&
      !path.startsWith("personnel."),
  );
  const startDate = (field: ManifestField) => (
    <ExtractedField marked={marked("businessStartDate")}>
      <TextField
        label={field.label}
        // The control follows the declared type (D-10 rule 1): any other is
        // a contract gap, kept usable as text rather than stranding the
        // broker.
        type={field.type === "date" ? "date" : "text"}
        value={b.businessStartDate}
        onChange={(e) =>
          edit("businessStartDate", { businessStartDate: e.target.value })
        }
        error={errorFor(errors, "businessStartDate")}
        disabled={busy}
        required
      />
    </ExtractedField>
  );
  const basicAnswers = basic.length > 0 && (
    <div className={TWO_COLUMNS}>
      {basic.map((f) => (
        <ExtractedField key={f.key} marked={extracted.isPending(f.key)}>
          <QuestionField
            field={f}
            value={draft.answers[f.key]}
            onChange={(value) => answer(f.key, value)}
            errors={errors}
            busy={busy}
            carriers={carriers}
          />
        </ExtractedField>
      ))}
    </div>
  );
  // A catalog that can't be read says so, with Retry; one with no Workers'
  // Comp manifest asks nothing more here, and PMS names anything still
  // missing at Submit.
  const catalogProblem = catalog.problem !== WC_MANIFEST_ABSENT &&
    catalog.problem && (
      <StepLoadProblem problem={catalog.problem} onRetry={catalog.retry} />
    );

  return (
    <div className="flex flex-col gap-4">
      <SectionCard title="Business information" titleAs="h3">
        {insuredEditable(draft.account, draft.submission) ? (
          <div className={TWO_COLUMNS}>
            <div className="sm:col-span-2">
              <ExtractedField marked={marked("legalName")}>
                <TextField
                  label="Legal name"
                  value={b.legalName}
                  onChange={(e) =>
                    edit("legalName", { legalName: e.target.value })
                  }
                  error={errorFor(errors, "legalName")}
                  disabled={busy}
                  required
                />
              </ExtractedField>
            </div>
            <div className="sm:col-span-2">
              <ExtractedField marked={marked("dbaName")}>
                <TextField
                  label="DBA name"
                  value={b.dbaName}
                  onChange={(e) => edit("dbaName", { dbaName: e.target.value })}
                  error={errorFor(errors, "dbaName")}
                  disabled={busy}
                  optional
                />
              </ExtractedField>
            </div>
            <ExtractedField marked={marked("state")}>
              <SearchSelect
                label="State"
                options={US_STATE_OPTIONS}
                placeholder="Choose a state"
                value={b.state}
                onChange={(state) => edit("state", { state })}
                error={errorFor(errors, "state")}
                // Set when the application starts; from then on it is the
                // mailing address's (the Locations step).
                disabled={busy || draft.account !== null}
                required
              />
              {draft.account !== null && (
                <p className="text-xs text-brk-dim">
                  Set when the application started. It follows the mailing
                  address on the Locations step.
                </p>
              )}
            </ExtractedField>
            <ExtractedField marked={marked("ein")}>
              <TextField
                label="FEIN"
                inputMode="numeric"
                placeholder="12-3456789"
                value={b.ein}
                onChange={(e) =>
                  edit("ein", { ein: formatEin(e.target.value) })
                }
                error={errorFor(errors, "ein")}
                disabled={busy}
                required
              />
            </ExtractedField>
            <ExtractedField marked={marked("entityType")}>
              <SelectField
                label="Legal entity"
                options={WC_ENTITY_TYPE_OPTIONS}
                placeholder="Choose the legal entity"
                value={b.entityType}
                onChange={(e) =>
                  edit("entityType", {
                    entityType:
                      WC_ENTITY_TYPE_OPTIONS.find(
                        (o) => o.value === e.target.value,
                      )?.value ?? "",
                  })
                }
                error={errorFor(errors, "entityType")}
                disabled={busy}
                required
              />
            </ExtractedField>
            <ExtractedField marked={marked("emailAddress")}>
              <TextField
                label="Business email"
                type="email"
                autoComplete="email"
                value={b.emailAddress}
                onChange={(e) =>
                  edit("emailAddress", { emailAddress: e.target.value })
                }
                error={errorFor(errors, "emailAddress")}
                disabled={busy}
                required
              />
            </ExtractedField>
            <ExtractedField marked={marked("phone")}>
              <PhoneField
                label="Business phone"
                value={b.phone}
                onChange={(phone) => edit("phone", { phone })}
                error={errorFor(errors, "phone")}
                required
              />
            </ExtractedField>
            {started && startDate(started)}
            {basicAnswers && (
              <div className="sm:col-span-2">{basicAnswers}</div>
            )}
          </div>
        ) : (
          <>
            <OnRecord
              business={b}
              // Shown as on record once it holds one; until then it's the
              // broker's to give, once.
              startDateLabel={
                started && draft.account?.businessStartDate
                  ? started.label
                  : undefined
              }
            />
            {started && !draft.account?.businessStartDate && (
              <div className={`mt-4 ${TWO_COLUMNS}`}>
                <div className="flex flex-col gap-1">
                  {startDate(started)}
                  <p className="text-xs text-brk-dim">
                    Not on record yet. Once saved, it changes only through an
                    endorsement.
                  </p>
                </div>
              </div>
            )}
            {basicAnswers && <div className="mt-4">{basicAnswers}</div>}
            {!b.ein.trim() && draft.account && (
              <p className="mt-3 text-body text-brk-danger">
                FEIN is required before pricing or submitting.{" "}
                <Link
                  className="text-brk-accent hover:underline"
                  to={`/accounts/${encodeURIComponent(draft.account.id)}`}
                >
                  Add the FEIN on the account’s Details tab
                </Link>
                .
              </p>
            )}
          </>
        )}
        {catalogProblem && <div className="mt-4">{catalogProblem}</div>}
      </SectionCard>
      <SectionCard title="Policy" titleAs="h3">
        <div className="sm:max-w-xs">
          <TextField
            label="Desired effective date"
            type="date"
            min={localTodayIso()}
            value={b.effectiveDate}
            onChange={(e) => set({ effectiveDate: e.target.value })}
            error={errorFor(errors, "effectiveDate")}
            disabled={busy}
            required
          />
        </div>
      </SectionCard>
      {elsewhere.length > 0 && (
        <ul className="list-disc pl-5 text-sm text-brk-danger">
          {elsewhere.map(([path, message]) => (
            <li key={path}>{message}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The insured as it is on record, read-only: each value in its field's box,
 *  where the field sits on the form (BRK-S349). */
function OnRecord({
  business: b,
  startDateLabel,
}: {
  business: WcBusiness;
  startDateLabel?: string;
}) {
  return (
    <div className="flex flex-col gap-4">
      <p className="text-body text-brk-dim">
        This insured is already on record. Its details change only through an
        endorsement, so they can’t be edited here.
      </p>
      <BusinessReadOnly business={b} startDateLabel={startDateLabel} />
    </div>
  );
}
